import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { fetchAuthSession } from 'aws-amplify/auth';
import { ToastrService } from 'ngx-toastr';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

/**
 * Reactive OEM-token refresh (SRA003-139 §5.2).
 *
 * On a 401 with `WWW-Authenticate: Bearer error="invalid_token"` from any
 * `oem-connection` API call, attempt one transparent re-auth via Amplify
 * (which refreshes the access token if the ID token is still valid), update
 * `localStorage.sra_user_credentials_access_token`, and replay the original
 * request exactly once. A second 401 surfaces a toast and routes to /signIn.
 *
 * Scope: the existing Bearer-attach for the OEM client is a lazy
 * `Configuration.credentials.cognito` hook in app.module.ts that reads
 * localStorage on every call. We do NOT introduce an interceptor for Bearer
 * attach (per the existing service-file note) - we only intercept the 401
 * path here. Limiting the scope to the OEM API URL keeps the rest of the
 * codebase's retry behaviour unchanged.
 */
@Injectable()
export class OemTokenRefreshInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private toastr: ToastrService,
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.isOemRequest(req)) {
      return next.handle(req);
    }
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (!this.isInvalidTokenError(err)) {
          return throwError(() => err);
        }
        return from(this.refreshToken()).pipe(
          switchMap((newToken) => {
            if (!newToken) {
              return this.routeToSignIn(err as HttpErrorResponse);
            }
            return next.handle(this.cloneWithBearer(req, newToken)).pipe(
              catchError((retryErr: unknown) => {
                if (this.isInvalidTokenError(retryErr)) {
                  return this.routeToSignIn(retryErr as HttpErrorResponse);
                }
                return throwError(() => retryErr);
              }),
            );
          }),
        );
      }),
    );
  }

  private isOemRequest(req: HttpRequest<unknown>): boolean {
    return req.url.includes('/oem-connection/');
  }

  private isInvalidTokenError(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse) || err.status !== 401) return false;
    const wwwAuth = err.headers?.get?.('WWW-Authenticate') ?? '';
    return /bearer/i.test(wwwAuth) && /error="?invalid_token"?/i.test(wwwAuth);
  }

  private async refreshToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (token) {
        localStorage.setItem(environment.keys.accessToken, token);
      }
      return token || null;
    } catch {
      return null;
    }
  }

  private cloneWithBearer(
    req: HttpRequest<unknown>,
    token: string,
  ): HttpRequest<unknown> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private routeToSignIn(err: HttpErrorResponse): Observable<never> {
    this.toastr.error('Your session expired. Please sign in again.');
    this.router.navigate(['/signIn']);
    return throwError(() => err);
  }
}
